import { Controller } from "react-hook-form";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CtaButton } from "@/components/common/CtaButton";
import { useLeadForm } from "@/forms/useLeadForm";
import { budgetOptions, serviceOptions } from "@/forms/schemas/lead.schema";
import { CheckCircle2, Loader2, ArrowRight } from "@/icons";
import { cn } from "@/lib/utils";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1.5 text-xs text-danger" data-testid="field-error">
      {message}
    </p>
  );
}

/** Complete, validated lead-capture form wired to the backend + email. */
export function LeadForm() {
  const { form, onSubmit, isSubmitting, isSuccess, sourceLabel, reset } =
    useLeadForm();
  const {
    register,
    control,
    formState: { errors },
  } = form;

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="lead-success"
        className="flex flex-col items-center gap-5 rounded-[1.5rem] border border-border bg-card p-10 text-center"
      >
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="h-7 w-7" />
        </span>
        <div>
          <h3 className="font-heading text-2xl font-bold text-foreground">
            Message received.
          </h3>
          <p className="mt-2 text-muted-foreground">
            Thank you for reaching out. A senior member of our team will get
            back to you within one business day.
          </p>
        </div>
        <button
          type="button"
          onClick={reset}
          data-testid="lead-reset"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          Send another message <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </motion.div>
    );
  }

  const inputCls =
    "h-12 rounded-xl border-border bg-background text-foreground placeholder:text-muted-foreground/70 focus-visible:ring-accent";

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="lead-form"
      className="rounded-[1.5rem] border border-border bg-card p-6 shadow-[0_18px_48px_-24px_rgba(15,23,42,0.18)] sm:p-8"
    >
      {sourceLabel && (
        <div
          data-testid="lead-source-banner"
          className="mb-6 flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/[0.06] px-4 py-3 text-sm text-foreground"
        >
          <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-accent" />
          <span>
            Enquiring about{" "}
            <span className="font-semibold text-primary">{sourceLabel}</span>
          </span>
        </div>
      )}
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="sm:col-span-1">
          <Label htmlFor="name" className="text-foreground">
            Full name
          </Label>
          <Input
            id="name"
            data-testid="input-name"
            placeholder="Jane Cooper"
            className={cn(inputCls, "mt-2")}
            {...register("name")}
          />
          <FieldError message={errors.name?.message} />
        </div>

        <div className="sm:col-span-1">
          <Label htmlFor="email" className="text-foreground">
            Work email
          </Label>
          <Input
            id="email"
            type="email"
            data-testid="input-email"
            placeholder="jane@company.com"
            className={cn(inputCls, "mt-2")}
            {...register("email")}
          />
          <FieldError message={errors.email?.message} />
        </div>

        <div className="sm:col-span-1">
          <Label htmlFor="company" className="text-foreground">
            Company <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="company"
            data-testid="input-company"
            placeholder="Acme Inc."
            className={cn(inputCls, "mt-2")}
            {...register("company")}
          />
        </div>

        <div className="sm:col-span-1">
          <Label className="text-foreground">
            Budget <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Controller
            control={control}
            name="budget"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  data-testid="select-budget"
                  className={cn(inputCls, "mt-2")}
                >
                  <SelectValue placeholder="Select a range" />
                </SelectTrigger>
                <SelectContent>
                  {budgetOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="sm:col-span-2">
          <Label className="text-foreground">
            What do you need help with?{" "}
            <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Controller
            control={control}
            name="service"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  data-testid="select-service"
                  className={cn(inputCls, "mt-2")}
                >
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {serviceOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="message" className="text-foreground">
            Project details
          </Label>
          <Textarea
            id="message"
            data-testid="input-message"
            rows={5}
            placeholder="Tell us about your goals, timeline and what success looks like…"
            className="mt-2 rounded-xl border-border bg-background text-foreground placeholder:text-muted-foreground/70 focus-visible:ring-accent"
            {...register("message")}
          />
          <FieldError message={errors.message?.message} />
        </div>
      </div>

      <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          By submitting you agree to our privacy policy. No spam, ever.
        </p>
        <CtaButton
          type="submit"
          size="lg"
          disabled={isSubmitting}
          withArrow={!isSubmitting}
          data-testid="lead-submit-button"
          className="w-full sm:w-auto"
        >
          {isSubmitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Sending…
            </span>
          ) : (
            "Book a Free Consultation"
          )}
        </CtaButton>
      </div>
    </form>
  );
}
